import {
  PPQ,
  PROJECT_FORMAT_VERSION,
  type AudioAssetRef,
  type AutomationCurve,
  type AutomationLane,
  type Clip,
  type ClipFade,
  type EffectInstance,
  type EffectType,
  type FadeCurve,
  type InstrumentState,
  type Marker,
  type MidiNote,
  type Project,
  type Send,
  type SnapValue,
  type TempoEvent,
  type TimeSignatureEvent,
  type Track,
  type TrackFreeze,
  type TrackKind,
  type ViewState,
  type WarpMarker,
  type AutomationMode,
} from '@/domain/types';
import {
  APP_VERSION,
  DEFAULT_TRACK_HEIGHT,
  MASTER_TRACK_ID,
  MAX_PITCH_SEMITONES,
  MAX_PLAYBACK_RATE,
  MAX_TRACK_HEIGHT,
  MAX_TRACK_HEIGHT_SCALE,
  MIN_PITCH_SEMITONES,
  MIN_PLAYBACK_RATE,
  MIN_TRACK_HEIGHT,
  MIN_TRACK_HEIGHT_SCALE,
  TRACK_COLORS,
  createEmptyProject,
  createMasterTrack,
} from '@/domain/project';
import { newGainPointId, newWarpMarkerId } from '@/util/id';
import { SNAP_VALUES } from '@/timing/snap';
import type { ClipGainPoint, WamParameterInfo } from '@/domain/types';
import { isWamUrl } from '@/engine/effects/wamHost';
import { TEMPO_RAMP_SHAPES } from '@/domain/types';
import { MAX_TAKE_NAME } from '@/domain/takes';
import { MAX_CLIP_GAIN, MIN_CLIP_GAIN, makeGainPoint, sortedGainPoints } from '@/domain/clipGain';
import {
  LoadReport,
  isRecord,
  readArray,
  readBool,
  readColor,
  readEnum,
  readId,
  readInt,
  readNumber,
  readNumberMap,
  readString,
} from './validate';

const TRACK_KINDS: readonly TrackKind[] = ['audio', 'instrument', 'bus', 'aux', 'master'];
const EFFECT_TYPES: readonly EffectType[] = ['eq3', 'compressor', 'reverb', 'delay', 'filter', 'limiter', 'chorus', 'saturator', 'wam'];
const FADE_CURVES: readonly FadeCurve[] = ['linear', 'equalPower', 'exponential'];
const AUTOMATION_CURVES: readonly AutomationCurve[] = ['linear', 'hold', 'smooth'];
const AUTOMATION_MODES: readonly AutomationMode[] = ['read', 'touch', 'latch', 'write'];

const BOTTOM_PANELS: readonly ViewState['bottomPanel'][] = ['none', 'mixer', 'pianoRoll', 'drumMachine', 'inspector'];

const MAX_TRACKS = 512;
const MAX_CLIPS = 20000;
const MAX_NOTES_PER_CLIP = 20000;
const MAX_POINTS_PER_LANE = 20000;
const MAX_TAKE_LANES = 64;
/** A ride with more points than this is a mistake or an attack, not a ride. */
const MAX_GAIN_POINTS = 2000;

function parseFade(raw: unknown): ClipFade {
  const r = isRecord(raw) ? raw : {};
  return {
    durationSec: readNumber(r.durationSec, 0, 0, 600),
    curve: readEnum(r.curve, FADE_CURVES, 'equalPower'),
    curvature: readNumber(r.curvature, 0, -1, 1),
  };
}

function parseSend(raw: unknown): Send | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  const target = readId(raw.targetTrackId);
  if (!id || !target) return null;
  return {
    id,
    targetTrackId: target,
    gain: readNumber(raw.gain, 0, 0, 4),
    preFader: readBool(raw.preFader, false),
    enabled: readBool(raw.enabled, true),
  };
}

function parseEffect(raw: unknown): EffectInstance | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  if (!id) return null;
  const type = readEnum(raw.type, EFFECT_TYPES, 'filter');
  // A room is only a reverb's, and only kept as an id this reader trusts; a
  // reference to an asset the project turns out not to hold is dropped later,
  // with the other dangling references.
  const impulseAssetId = type === 'reverb' ? readId(raw.impulseAssetId) : null;
  // A hosted plugin without a URL to fetch is nothing; it is dropped rather
  // than kept as an insert that can never load.
  const wamUrl = type === 'wam' && typeof raw.wamUrl === 'string' && isWamUrl(raw.wamUrl) ? raw.wamUrl : null;
  if (type === 'wam' && !wamUrl) return null;
  return {
    id,
    type,
    enabled: readBool(raw.enabled, true),
    params: readNumberMap(raw.params),
    ...(impulseAssetId ? { impulseAssetId } : {}),
    ...(wamUrl
      ? {
          wamUrl,
          wamName: readString(raw.wamName, 'Web Audio Module', 128),
          wamParameters: readWamParameters(raw.wamParameters),
          ...(readWamState(raw.wamState) !== undefined ? { wamState: readWamState(raw.wamState) } : {}),
        }
      : {}),
  };
}

/** A plugin's saved state: any JSON, kept as it is if it is not absurdly large. */
const MAX_WAM_STATE_BYTES = 1_000_000;
export function readWamState(raw: unknown): unknown {
  if (raw === undefined || raw === null) return undefined;
  try {
    const text = JSON.stringify(raw);
    if (text === undefined || text.length > MAX_WAM_STATE_BYTES) return undefined;
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** The plugin's parameter list as it was saved, read defensively. */
function readWamParameters(raw: unknown): WamParameterInfo[] {
  const out: WamParameterInfo[] = [];
  for (const entry of readArray(raw).slice(0, 256)) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || entry.id.length === 0 || entry.id.length > 64) continue;
    const min = readNumber(entry.min, 0);
    const max = readNumber(entry.max, 1);
    if (!(max > min)) continue;
    out.push({
      id: entry.id,
      label: readString(entry.label, entry.id, 64),
      min,
      max,
      default: readNumber(entry.default, min, min, max),
      units: readString(entry.units, '', 16),
    });
  }
  return out;
}

function parseInstrument(raw: unknown, report: LoadReport): InstrumentState | null {
  if (!isRecord(raw)) return null;
  const type = readEnum(raw.type, ['synth', 'drumMachine', 'sampler', 'wam'] as const, 'synth');
  const data = isRecord(raw.data) ? (raw.data as Record<string, unknown>) : {};
  if (type === 'wam') {
    // A plugin with nowhere to be fetched from is not an instrument; the
    // track keeps its notes on the synth instead, and the report says so.
    if (typeof data.wamUrl !== 'string' || !isWamUrl(data.wamUrl)) {
      report.warn('An instrument track named a plugin with no address to load it from; it plays the synth instead.');
      return { type: 'synth', params: {}, data: {} };
    }
    return {
      type,
      params: readNumberMap(raw.params),
      data: {
        wamUrl: data.wamUrl,
        wamName: readString(data.wamName, 'Web Audio Module', 128),
        wamParameters: readWamParameters(data.wamParameters),
        ...(readWamState(data.wamState) !== undefined ? { wamState: readWamState(data.wamState) } : {}),
      },
    };
  }
  return { type, params: readNumberMap(raw.params), data };
}

function parseTrack(raw: unknown, index: number, report: LoadReport): Track | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  if (!id) {
    report.warn('Skipped a track with a missing or invalid id.');
    return null;
  }
  const kind = readEnum(raw.kind, TRACK_KINDS, 'audio');
  return {
    id,
    kind,
    name: readString(raw.name, `Track ${index + 1}`, 128),
    color: readColor(raw.color, TRACK_COLORS[index % TRACK_COLORS.length]),
    height: readInt(raw.height, DEFAULT_TRACK_HEIGHT, MIN_TRACK_HEIGHT, MAX_TRACK_HEIGHT),
    volume: readNumber(raw.volume, 1, 0, 4),
    pan: readNumber(raw.pan, 0, -1, 1),
    mute: readBool(raw.mute, false),
    solo: readBool(raw.solo, false),
    armed: readBool(raw.armed, false),
    inputMonitor: readBool(raw.inputMonitor, false),
    inputDeviceId: typeof raw.inputDeviceId === 'string' ? readString(raw.inputDeviceId, '', 256) || null : null,
    outputTrackId: kind === 'master' ? null : readId(raw.outputTrackId),
    sends: readArray(raw.sends).map(parseSend).filter((s): s is Send => s !== null),
    effects: readArray(raw.effects).map(parseEffect).filter((e): e is EffectInstance => e !== null),
    instrument: parseInstrument(raw.instrument, report),
    clipIds: readArray(raw.clipIds).map(readId).filter((v): v is string => v !== null),
    automationLaneIds: readArray(raw.automationLaneIds).map(readId).filter((v): v is string => v !== null),
    collapsed: readBool(raw.collapsed, false),
    automationMode: readEnum(raw.automationMode, AUTOMATION_MODES, 'read'),
    showAutomation: readBool(raw.showAutomation, false),
    showTakeLanes: readBool(raw.showTakeLanes, false),
    takeNames: parseTakeNames(raw.takeNames),
    freeze: parseFreeze(raw.freeze),
  };
}

/**
 * A clip's gain envelope off disk.
 *
 * Points with no usable position or level are dropped rather than repaired: a
 * point at NaN seconds would poison every comparison downstream, and a clip is
 * better off with a shorter ride than with an envelope that cannot be evaluated.
 * The list is capped and sorted, because nothing later assumes a file was
 * written by this program.
 */
function parseGainEnvelope(raw: unknown): ClipGainPoint[] {
  const points: ClipGainPoint[] = [];
  for (const entry of readArray(raw).slice(0, MAX_GAIN_POINTS)) {
    if (!isRecord(entry)) continue;
    const id = readId(entry.id) ?? newGainPointId();
    if (typeof entry.sourceSec !== 'number' || !Number.isFinite(entry.sourceSec)) continue;
    points.push(
      makeGainPoint(id, entry.sourceSec, readNumber(entry.gain, 1, MIN_CLIP_GAIN, MAX_CLIP_GAIN), readEnum(entry.curve, AUTOMATION_CURVES, 'linear')),
    );
  }
  return sortedGainPoints(points);
}

/**
 * Take names off disk.
 *
 * The keys are lane numbers written as strings, and a file is not obliged to be
 * honest about that: anything that is not a plain positive lane number, or whose
 * value is not a string, is dropped rather than trusted. Names are also clamped
 * in length, because a project file is an untrusted import like any other and a
 * megabyte-long lane label is not a name.
 */
function parseTakeNames(raw: unknown): Record<string, string> {
  if (!isRecord(raw)) return {};
  const out: Record<string, string> = {};
  let count = 0;
  for (const [key, value] of Object.entries(raw)) {
    if (count >= MAX_TAKE_LANES) break;
    const lane = Number(key);
    if (!Number.isInteger(lane) || lane < 1 || lane > MAX_TAKE_LANES) continue;
    const name = readString(value, '', MAX_TAKE_NAME).trim();
    if (!name) continue;
    out[String(lane)] = name;
    count++;
  }
  return out;
}

/**
 * A freeze record only survives loading if it names an asset; without one there
 * is nothing to play, and a track claiming to be frozen with no audio would be
 * silent for no visible reason.
 */
function parseFreeze(raw: unknown): TrackFreeze | null {
  if (!isRecord(raw)) return null;
  const assetId = readId(raw.assetId);
  if (!assetId) return null;
  return {
    assetId,
    startTicks: readInt(raw.startTicks, 0, 0, Number.MAX_SAFE_INTEGER),
    lengthTicks: readInt(raw.lengthTicks, 0, 0, Number.MAX_SAFE_INTEGER),
    signature: typeof raw.signature === 'string' ? raw.signature.slice(0, 200000) : '',
    createdAt: readInt(raw.createdAt, Date.now(), 0, Number.MAX_SAFE_INTEGER),
  };
}

function parseNote(raw: unknown): MidiNote | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  if (!id) return null;
  return {
    id,
    pitch: readInt(raw.pitch, 60, 0, 127),
    velocity: readInt(raw.velocity, 100, 1, 127),
    startTicks: readInt(raw.startTicks, 0, 0, Number.MAX_SAFE_INTEGER),
    durationTicks: readInt(raw.durationTicks, PPQ, 1, Number.MAX_SAFE_INTEGER),
    channel: readInt(raw.channel, 0, 0, 15),
  };
}

/**
 * A clip read defensively from unknown data.
 *
 * Exported because the clipboard needs it: material pasted into a project is
 * validated exactly as material loaded from a file is, so a command boundary
 * behaves the same way wherever the data came from.
 */
export function parseClipValue(raw: unknown): Clip | null {
  return parseClip(raw, new LoadReport());
}

function parseClip(raw: unknown, report: LoadReport): Clip | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  const trackId = readId(raw.trackId);
  if (!id || !trackId) {
    report.warn('Skipped a clip with a missing id or track reference.');
    return null;
  }
  const base = {
    id,
    trackId,
    name: readString(raw.name, 'Clip', 128),
    startTicks: readInt(raw.startTicks, 0, 0, Number.MAX_SAFE_INTEGER),
    muted: readBool(raw.muted, false),
    color: typeof raw.color === 'string' ? readColor(raw.color, '') || null : null,
    takeLane: readInt(raw.takeLane, 0, 0, MAX_TAKE_LANES),
  };

  if (raw.kind === 'midi') {
    const duration = readInt(raw.durationTicks, PPQ * 4, 1, Number.MAX_SAFE_INTEGER);
    return {
      ...base,
      kind: 'midi',
      durationTicks: duration,
      notes: readArray(raw.notes).slice(0, MAX_NOTES_PER_CLIP).map(parseNote).filter((n): n is MidiNote => n !== null),
      loop: readBool(raw.loop, false),
      loopLengthTicks: readInt(raw.loopLengthTicks, duration, 1, Number.MAX_SAFE_INTEGER),
    };
  }

  const assetId = readId(raw.assetId);
  if (!assetId) {
    report.warn(`Clip "${base.name}" references no audio asset and was skipped.`);
    return null;
  }
  return {
    ...base,
    kind: 'audio',
    assetId,
    offsetSec: readNumber(raw.offsetSec, 0, 0, 24 * 3600),
    durationSec: readNumber(raw.durationSec, 0, 0, 24 * 3600),
    gain: readNumber(raw.gain, 1, 0, 8),
    gainEnvelope: parseGainEnvelope(raw.gainEnvelope),
    fadeIn: parseFade(raw.fadeIn),
    fadeOut: parseFade(raw.fadeOut),
    playbackRate: readNumber(raw.playbackRate, 1, MIN_PLAYBACK_RATE, MAX_PLAYBACK_RATE),
    preservePitch: readBool(raw.preservePitch, true),
    warpSourceBpm: raw.warpSourceBpm === null || raw.warpSourceBpm === undefined
      ? null
      : readNumber(raw.warpSourceBpm, 120, 5, 900),
    warpMarkers: readArray(raw.warpMarkers)
      .map((m): WarpMarker | null =>
        isRecord(m)
          ? {
              id: readId(m.id) ?? newWarpMarkerId(),
              sourceSec: readNumber(m.sourceSec, 0, 0, 60 * 60 * 4),
              beat: readNumber(m.beat, 0, 0, 100000),
            }
          : null,
      )
      .filter((m): m is WarpMarker => m !== null)
      .slice(0, 512),
    reversed: readBool(raw.reversed, false),
    pitchSemitones: readNumber(raw.pitchSemitones, 0, MIN_PITCH_SEMITONES, MAX_PITCH_SEMITONES),
    formantSemitones: parseFormants(raw),
  };
}

/**
 * Where a clip's formants sit, read from either shape this field has had.
 *
 * It began as a boolean — hold them, or let them follow — and became a position
 * so they could be moved somewhere else entirely. A project written under the
 * older shape says `preserveFormants: true`, which is exactly "at zero semitones
 * from where they were", so it converts rather than being dropped. A project
 * older than both says neither, and gets the behaviour it was saved with: the
 * formants follow the pitch.
 */
function parseFormants(raw: Record<string, unknown>): number | null {
  if (raw.formantSemitones !== undefined && raw.formantSemitones !== null) {
    return readNumber(raw.formantSemitones, 0, MIN_PITCH_SEMITONES, MAX_PITCH_SEMITONES);
  }
  if (raw.formantSemitones === null) return null;
  return readBool(raw.preserveFormants, false) ? 0 : null;
}

function parseLane(raw: unknown): AutomationLane | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  const target = isRecord(raw.target) ? raw.target : null;
  const trackId = target ? readId(target.trackId) : null;
  if (!id || !target || !trackId) return null;
  return {
    id,
    target: {
      kind: readEnum(target.kind, ['track', 'effect', 'instrument'] as const, 'track'),
      trackId,
      effectId: readId(target.effectId),
      paramId: readString(target.paramId, 'volume', 64),
    },
    enabled: readBool(raw.enabled, true),
    height: readInt(raw.height, 56, 24, 240),
    points: readArray(raw.points)
      .slice(0, MAX_POINTS_PER_LANE)
      .map((p) => {
        if (!isRecord(p)) return null;
        const pid = readId(p.id);
        if (!pid) return null;
        return {
          id: pid,
          tick: readInt(p.tick, 0, 0, Number.MAX_SAFE_INTEGER),
          value: readNumber(p.value, 0, 0, 1),
          curve: readEnum(p.curve, AUTOMATION_CURVES, 'linear'),
        };
      })
      .filter((p): p is AutomationLane['points'][number] => p !== null)
      .sort((a, b) => a.tick - b.tick),
  };
}

function parseAsset(raw: unknown): AudioAssetRef | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  if (!id) return null;
  return {
    id,
    name: readString(raw.name, 'Audio', 256),
    originalFileName: typeof raw.originalFileName === 'string' ? readString(raw.originalFileName, '', 256) || null : null,
    mimeType: readString(raw.mimeType, 'audio/wav', 128),
    byteLength: readInt(raw.byteLength, 0, 0, Number.MAX_SAFE_INTEGER),
    sampleRate: readInt(raw.sampleRate, 48000, 1, 768000),
    channelCount: readInt(raw.channelCount, 2, 1, 32),
    durationSec: readNumber(raw.durationSec, 0, 0, 24 * 3600),
    createdAt: readInt(raw.createdAt, Date.now(), 0, Number.MAX_SAFE_INTEGER),
    recorded: readBool(raw.recorded, false),
  };
}

function parseMarker(raw: unknown): Marker | null {
  if (!isRecord(raw)) return null;
  const id = readId(raw.id);
  if (!id) return null;
  return {
    id,
    name: readString(raw.name, 'Marker', 128),
    tick: readInt(raw.tick, 0, 0, Number.MAX_SAFE_INTEGER),
    color: readColor(raw.color, '#e0a13c'),
  };
}

/**
 * Turns an arbitrary parsed-JSON value into a structurally valid Project.
 *
 * The result is guaranteed to satisfy the DAW's internal invariants: a master
 * track exists, every clip belongs to an existing track, every `clipIds` entry
 * resolves, routing never dangles, and the tempo/meter maps start at tick 0.
 */
export function parseProject(raw: unknown, report = new LoadReport()): { project: Project; report: LoadReport } {
  if (!isRecord(raw)) {
    report.warn('Project data was not an object; created an empty project instead.');
    return { project: createEmptyProject(), report };
  }

  const metaRaw = isRecord(raw.meta) ? raw.meta : {};
  const transportRaw = isRecord(raw.transport) ? raw.transport : {};
  const viewRaw = isRecord(raw.view) ? raw.view : {};

  const ppq = readInt(transportRaw.ppq, PPQ, 24, 30720);

  const tempoMap: TempoEvent[] = readArray(transportRaw.tempoMap)
    .map((e): TempoEvent | null =>
      isRecord(e)
        ? {
            tick: readInt(e.tick, 0, 0, Number.MAX_SAFE_INTEGER),
            bpm: readNumber(e.bpm, 120, 5, 900),
            ...(readBool(e.ramp, false) ? { ramp: true } : {}),
            ...(readBool(e.ramp, false) && readEnum(e.rampShape, TEMPO_RAMP_SHAPES, 'linear') !== 'linear'
              ? { rampShape: 'exponential' as const }
              : {}),
          }
        : null,
    )
    .filter((e): e is TempoEvent => e !== null);
  if (tempoMap.length === 0) tempoMap.push({ tick: 0, bpm: 120 });

  const timeSignatures: TimeSignatureEvent[] = readArray(transportRaw.timeSignatures)
    .map((e) =>
      isRecord(e)
        ? {
            tick: readInt(e.tick, 0, 0, Number.MAX_SAFE_INTEGER),
            numerator: readInt(e.numerator, 4, 1, 64),
            denominator: readInt(e.denominator, 4, 1, 64),
          }
        : null,
    )
    .filter((e): e is TimeSignatureEvent => e !== null);
  if (timeSignatures.length === 0) timeSignatures.push({ tick: 0, numerator: 4, denominator: 4 });

  // --- tracks -------------------------------------------------------------
  const tracks: Record<string, Track> = {};
  const tracksRaw = isRecord(raw.tracks) ? raw.tracks : {};
  let index = 0;
  for (const value of Object.values(tracksRaw)) {
    if (index >= MAX_TRACKS) {
      report.warn(`Project contained more than ${MAX_TRACKS} tracks; the excess was dropped.`);
      break;
    }
    const track = parseTrack(value, index, report);
    if (track) {
      tracks[track.id] = track;
      index++;
    }
  }

  let masterTrackId = readId(raw.masterTrackId) ?? MASTER_TRACK_ID;
  if (!tracks[masterTrackId] || tracks[masterTrackId].kind !== 'master') {
    const existingMaster = Object.values(tracks).find((t) => t.kind === 'master');
    if (existingMaster) {
      masterTrackId = existingMaster.id;
    } else {
      const master = createMasterTrack();
      tracks[master.id] = master;
      masterTrackId = master.id;
      report.warn('Project had no master track; one was created.');
    }
  }
  tracks[masterTrackId].kind = 'master';
  tracks[masterTrackId].outputTrackId = null;

  // Repair routing: unknown or self-referential destinations fall back to master.
  for (const track of Object.values(tracks)) {
    if (track.id === masterTrackId) continue;
    if (!track.outputTrackId || !tracks[track.outputTrackId] || track.outputTrackId === track.id) {
      track.outputTrackId = masterTrackId;
    }
    track.sends = track.sends.filter((s) => tracks[s.targetTrackId] && s.targetTrackId !== track.id);
  }

  let trackOrder = readArray(raw.trackOrder)
    .map(readId)
    .filter((id): id is string => id !== null && Boolean(tracks[id]) && id !== masterTrackId);
  const ordered = new Set(trackOrder);
  for (const id of Object.keys(tracks)) {
    if (id !== masterTrackId && !ordered.has(id)) trackOrder.push(id);
  }
  trackOrder = [...new Set(trackOrder)];

  // --- clips --------------------------------------------------------------
  const clips: Record<string, Clip> = {};
  const clipsRaw = isRecord(raw.clips) ? raw.clips : {};
  let clipCount = 0;
  for (const value of Object.values(clipsRaw)) {
    if (clipCount >= MAX_CLIPS) {
      report.warn(`Project contained more than ${MAX_CLIPS} clips; the excess was dropped.`);
      break;
    }
    const clip = parseClip(value, report);
    if (clip && tracks[clip.trackId]) {
      clips[clip.id] = clip;
      clipCount++;
    } else if (clip) {
      report.warn(`Clip "${clip.name}" pointed at a missing track and was dropped.`);
    }
  }

  // --- assets -------------------------------------------------------------
  const assets: Record<string, AudioAssetRef> = {};
  const assetsRaw = isRecord(raw.assets) ? raw.assets : {};
  for (const value of Object.values(assetsRaw)) {
    const asset = parseAsset(value);
    if (asset) assets[asset.id] = asset;
  }

  // A freeze whose rendered audio has gone is discarded, so the track goes back
  // to playing its own material rather than being silently silent.
  for (const track of Object.values(tracks)) {
    if (track.freeze && !assets[track.freeze.assetId]) {
      report.warn(`The frozen render for "${track.name}" was missing, so the track was unfrozen.`);
      track.freeze = null;
    }
  }

  // A reverb whose room has gone goes back to its synthesised tail, and says so.
  for (const track of Object.values(tracks)) {
    for (const effect of track.effects) {
      if (effect.impulseAssetId && !assets[effect.impulseAssetId]) {
        report.warn(`The room recording for a reverb on "${track.name}" was missing, so it uses the synthesised tail.`);
        delete effect.impulseAssetId;
      }
    }
  }

  // Drop audio clips whose asset is missing, so the timeline never shows silence
  // that the user cannot explain.
  for (const clip of Object.values(clips)) {
    if (clip.kind === 'audio' && !assets[clip.assetId]) {
      report.warn(`Clip "${clip.name}" referenced a missing audio asset and was dropped.`);
      delete clips[clip.id];
    }
  }

  // --- automation ---------------------------------------------------------
  const automation: Record<string, AutomationLane> = {};
  const autoRaw = isRecord(raw.automation) ? raw.automation : {};
  for (const value of Object.values(autoRaw)) {
    const lane = parseLane(value);
    if (lane && tracks[lane.target.trackId]) automation[lane.id] = lane;
  }

  // --- rebuild ownership lists from the entities themselves ----------------
  for (const track of Object.values(tracks)) {
    track.clipIds = [];
    track.automationLaneIds = [];
  }
  for (const clip of Object.values(clips)) tracks[clip.trackId].clipIds.push(clip.id);
  for (const track of Object.values(tracks)) {
    track.clipIds.sort((a, b) => clips[a].startTicks - clips[b].startTicks);
  }
  for (const lane of Object.values(automation)) tracks[lane.target.trackId].automationLaneIds.push(lane.id);

  const now = Date.now();
  const project: Project = {
    meta: {
      id: readId(metaRaw.id) ?? createEmptyProject().meta.id,
      name: readString(metaRaw.name, 'Untitled Project', 200),
      createdAt: readInt(metaRaw.createdAt, now, 0, Number.MAX_SAFE_INTEGER),
      modifiedAt: readInt(metaRaw.modifiedAt, now, 0, Number.MAX_SAFE_INTEGER),
      formatVersion: PROJECT_FORMAT_VERSION,
      appVersion: readString(metaRaw.appVersion, APP_VERSION, 32),
    },
    transport: {
      ppq,
      tempoMap,
      timeSignatures,
      loopEnabled: readBool(transportRaw.loopEnabled, false),
      loopStartTicks: readInt(transportRaw.loopStartTicks, 0, 0, Number.MAX_SAFE_INTEGER),
      loopEndTicks: readInt(transportRaw.loopEndTicks, ppq * 16, 1, Number.MAX_SAFE_INTEGER),
      metronomeEnabled: readBool(transportRaw.metronomeEnabled, false),
      countInBars: readInt(transportRaw.countInBars, 1, 0, 8),
      snap: readEnum<SnapValue>(transportRaw.snap, SNAP_VALUES, '1/4'),
      crossfadeSec: readNumber(transportRaw.crossfadeSec, 0.01, 0, 10),
      playheadTicks: readInt(transportRaw.playheadTicks, 0, 0, Number.MAX_SAFE_INTEGER),
    },
    tracks,
    trackOrder,
    masterTrackId,
    clips,
    automation,
    assets,
    markers: readArray(raw.markers).map(parseMarker).filter((m): m is Marker => m !== null),
    view: {
      pixelsPerBeat: readNumber(viewRaw.pixelsPerBeat, 32, 2, 800),
      scrollTicks: readInt(viewRaw.scrollTicks, 0, 0, Number.MAX_SAFE_INTEGER),
      scrollTop: readInt(viewRaw.scrollTop, 0, 0, 1_000_000),
      trackHeightScale: readNumber(viewRaw.trackHeightScale, 1, MIN_TRACK_HEIGHT_SCALE, MAX_TRACK_HEIGHT_SCALE),
      bottomPanel: readEnum(viewRaw.bottomPanel, BOTTOM_PANELS, 'none'),
    },
  };

  if (project.transport.loopEndTicks <= project.transport.loopStartTicks) {
    project.transport.loopEndTicks = project.transport.loopStartTicks + ppq * 4;
  }

  return { project, report };
}
